import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import RouteTransition from './components/layout/RouteTransition';
import ThemeBackdrop from './components/layout/ThemeBackdrop';
import { ThemeProvider } from './utils/ThemeContext';

import './App.css';
import HomePage from './pages/home/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import UploadAnimeComponent from './pages/upload/UploadPage';
import LegalPage from './pages/LegalPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminPage from './pages/admin/AdminPage';
import AnimePage from './pages/anime/AnimePage';
import WatchPage from './pages/watch/WatchPage';
import PrivacyPage from './pages/PrivacyPage';
import AllAnimePage from './pages/AllAnimePage';
import CategoryManagementPage from './pages/CategoryManagementPage';
import CategoryPage from './pages/CategoryPage';
import ProfilePage from './pages/profile/ProfilePage';
import CollectionPage from './pages/CollectionPage';
import LeaderboardPage from './pages/leaderboard/LeaderboardPage';
import AdminUsersPage from './pages/admin/AdminUsers';
import ReviewsAdminPage from './pages/admin/ReviewsAdminPage';
import AboutPage from './pages/AboutPage';
import CommunityGuidelinesPage from './pages/CommunityGuidelines';
import ProfileSettingsPage from './pages/profile_settings/ProfileSettingsPage';
import TestWatchPage from './pages/watch/TestWatchPage';
import TestWatchPageAdFox from './pages/watch/TestWatchPageAdfox';
import AdFoxInstreamTest from './pages/watch/AdFoxInstreamTest';
import AdFoxInstreamTest2 from './pages/watch/AdFoxInstreamTest2';
import VastTestPage from './pages/watch/VastTest';
import VastAdPlayer from './pages/watch/VastTest';
import ContactPage from './pages/ContactPage';
import AppLoginPage from './pages/AppLoginPage';
import CategoriesPage from './pages/CategoriesPage';
import SearchPage from './pages/search/SearchPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import MangaPage from './pages/manga/MangaPage';
import MangaDetailsPage from './pages/manga/MangaDetailsPage';
import MangaReaderPage from './pages/manga/MangaReaderPage';
import MangaStudioPage from './pages/manga/MangaStudioPage';
import MangaStudioTeamPage from './pages/manga/MangaStudioTeamPage';
import MangaStudioMangaPage from './pages/manga/MangaStudioMangaPage';
import MangaStudioChaptersPage from './pages/manga/MangaStudioChaptersPage';
import MangaTeamPage from './pages/manga/MangaTeamPage';
import MangaModerationPage from './pages/admin/MangaModerationPage';
import KodikModerationPage from './pages/admin/KodikModerationPage';
import MangaRulesPage from './pages/manga/MangaRulesPage';
import MangaUserListsPage from './pages/manga/MangaUserListsPage';
import RequireSession from './components/RequireSession';
import ModerationPage from './pages/admin/ModerationPage';
import './components/popup/popups.css';

function App() {
    const [rateLimitNotice, setRateLimitNotice] = useState(null);

    useEffect(() => {
        const handleRateLimit = (event) => {
            const detail = event?.detail || {};
            if (!detail?.message) {
                return;
            }
            setRateLimitNotice({
                message: detail.message,
                retryAfter: detail.retryAfter || null,
                ts: Date.now(),
            });
        };
        window.addEventListener('manga-rate-limit', handleRateLimit);
        return () => window.removeEventListener('manga-rate-limit', handleRateLimit);
    }, []);

    useEffect(() => {
        if (!rateLimitNotice) {
            return undefined;
        }
        const timer = setTimeout(() => setRateLimitNotice(null), 6000);
        return () => clearTimeout(timer);
    }, [rateLimitNotice]);

    return (
        <ThemeProvider>
        <div className="App">
            <ThemeBackdrop />
            {rateLimitNotice ? (
                <div className="manga-rate-limit-toast">
                    {rateLimitNotice.message}
                </div>
            ) : null}
            <Router>
                <RouteTransition>
                <Routes>
                    <Route path="/" element={<HomePage/>}/>
                    <Route path="/login" element={<LoginPage/>}/>
                    <Route path="/profile/" element={<ProfilePage/>}/>
                    <Route path="/settings" element={<ProfileSettingsPage/>}/>
                    <Route path="/profile/:nickname" element={<ProfilePage/>}/>
                    <Route path="/all" element={<AllAnimePage/>}/>
                    <Route path="/categories" element={<CategoriesPage/>}/>
                    <Route path="/manga" element={<MangaPage/>}/>
                    <Route path="/manga/studio" element={<MangaStudioPage/>}/>
                    <Route path="/manga/studio/team" element={<MangaStudioTeamPage/>}/>
                    <Route path="/manga/studio/manga" element={<MangaStudioMangaPage/>}/>
                    <Route path="/manga/studio/chapters" element={<MangaStudioChaptersPage/>}/>
                    <Route path="/manga/rules" element={<MangaRulesPage/>}/>
                    <Route path="/manga/teams/:teamId" element={<MangaTeamPage/>}/>
                    <Route path="/manga/lists/:userId" element={<MangaUserListsPage/>}/>
                    <Route
                        path="/manga/:slug/read"
                        element={
                            <RequireSession fallback={({ slug }) => `/manga/${slug}`}>
                                <MangaReaderPage />
                            </RequireSession>
                        }
                    />
                    <Route path="/manga/:slug" element={<MangaDetailsPage/>}/>
                    <Route path="/register" element={<RegisterPage/>}/>
                    <Route path="/upload" element={<UploadAnimeComponent/>}/>
                    <Route path="/legal" element={<LegalPage/>}/>
                    <Route path="/about" element={<AboutPage/>}/>
                    <Route path="/community" element={<CommunityGuidelinesPage/>}/>
                    <Route path="/admin" element={<AdminPage/>}/>
                    <Route path="/admin/kodik" element={<KodikModerationPage/>}/>
                    <Route path="/admin/users" element={<AdminUsersPage/>}/>
                    <Route path="/admin/reviews" element={<ReviewsAdminPage/>}/>
                    <Route path="/admin/moderation" element={<ModerationPage/>}/>
                    <Route path="/admin/manga" element={<MangaModerationPage/>}/>
                    <Route path="/admin/category" element={<CategoryManagementPage/>}/>
                    <Route path="/leaderboard" element={<LeaderboardPage/>}/>
                    <Route path="/privacy" element={<PrivacyPage/>}/>
                    <Route path="/anime/:id" element={<AnimePage/>}/>
                    <Route path="/category/:id" element={<CategoryPage/>}/>
                    <Route path="/collection/:id" element={<CollectionPage/>}/>
                    <Route
                        path="/watch/:id/:seriesId"
                        element={
                            <RequireSession fallback={({ id }) => `/anime/${id}`}>
                                <WatchPage />
                            </RequireSession>
                        }
                    />
                    <Route
                        path="/testwatch/:id/:seriesId"
                        element={
                            <RequireSession fallback={({ id }) => `/anime/${id}`}>
                                <TestWatchPage />
                            </RequireSession>
                        }
                    />
                    <Route
                        path="/testwatchadfox/:id/:seriesId"
                        element={
                            <RequireSession fallback={({ id }) => `/anime/${id}`}>
                                <TestWatchPageAdFox />
                            </RequireSession>
                        }
                    />
                    <Route path="/testad" element={<AdFoxInstreamTest/>}/>
                    <Route path="/testad2" element={<AdFoxInstreamTest2/>}/>
                    <Route path="/vasttest" element={<VastAdPlayer/>}/>
                    <Route path="/contact" element={<ContactPage/>}/>
                    <Route path="/account-deletion" element={<AccountDeletionPage/>}/>
                    <Route path="/search" element={<SearchPage/>}/>
                    <Route path="/appLogin" element={<AppLoginPage />} /> 
                    <Route path="*" element={<NotFoundPage/>}/>
                </Routes>
                </RouteTransition>
            </Router>
        </div>
        </ThemeProvider>
    );
}

export default App;
